import { buildUrl, deleteRequest, getData, postData, putData } from './api';
import { createPTDate } from '../utils/timezoneUtils';
import { TaskCategory, TaskStatus } from '../types/task';

export interface Task {
  id: string;
  title: string;
  linkUrl?: string | null;
  status: TaskStatus;
  dueDate: string;
  category: TaskCategory;
  isPriority: boolean;
  createdAt: string;
  completedAt: string | null;
  isRolledOver: boolean;
  displayOrder: number;
  userId: string;
}

export interface CreateTaskDTO {
  title: string;
  linkUrl?: string;
  status: TaskStatus;
  dueDate: string;
  category: TaskCategory;
  isPriority?: boolean;
  displayOrder: number;
  isRolledOver?: boolean;
}

export interface UpdateTaskDTO extends Partial<CreateTaskDTO> {}

export interface MoveTaskDTO {
  dueDate: string;
  isRolledOver?: boolean;
}

export interface ReorderTaskDTO {
  displayOrder: number;
}

export interface BulkMoveDTO {
  taskIds: string[];
  targetDate: string;
}

export interface BulkMoveResponse {
  success: boolean;
  movedCount: number;
  tasks: Task[];
  errors?: Array<{ taskId: string; error: string }>;
}

export const todoApi = {
  fetchTasks(options?: {
    status?: string,
    skip?: number,
    take?: number,
    startDate?: string,
    endDate?: string
  }): Promise<Task[]> {
    return getData<Task[]>(buildUrl('/api/tasks', options));
  },

  fetchTasksByDate(date: Date): Promise<Task[]> {
    const formattedDate = createPTDate(date).toISOString().split('T')[0];
    return getData<Task[]>(`/api/tasks/${formattedDate}`);
  },

  createTask(task: CreateTaskDTO): Promise<Task> {
    const ptDate = createPTDate(task.dueDate);

    return postData<Task, CreateTaskDTO>('/api/tasks', {
      ...task,
      dueDate: ptDate.toISOString(),
    });
  },

  updateTask(id: string, task: UpdateTaskDTO): Promise<Task> {
    const updatedTask = { ...task };
    if (updatedTask.dueDate) {
      updatedTask.dueDate = createPTDate(updatedTask.dueDate).toISOString();
    }

    return putData<Task, UpdateTaskDTO>(`/api/tasks/${id}`, updatedTask);
  },

  deleteTask(id: string): Promise<void> {
    return deleteRequest(`/api/tasks/${id}`);
  },

  async moveTask(id: string, moveData: MoveTaskDTO): Promise<Task> {
    const ptMoveData = {
      ...moveData,
      dueDate: createPTDate(moveData.dueDate).toISOString(),
    };

    try {
      return await putData<Task, MoveTaskDTO>(`/api/tasks/${id}/move`, ptMoveData);
    } catch (error) {
      console.error('todoApi.moveTask error:', error);
      throw error;
    }
  },

  reorderTask(id: string, reorderData: ReorderTaskDTO): Promise<Task> {
    return putData<Task, ReorderTaskDTO>(`/api/tasks/${id}/reorder`, reorderData);
  },

  async getTaskCount(options?: {
    status?: string,
    startDate?: string,
    endDate?: string
  }): Promise<number> {
    return (await getData<{ count: number }>(buildUrl('/api/tasks/count', options))).count;
  },

  async bulkMoveTasks(data: BulkMoveDTO): Promise<BulkMoveResponse> {
    const ptData = {
      taskIds: data.taskIds,
      targetDate: createPTDate(data.targetDate).toISOString(),
    };

    try {
      return await postData<BulkMoveResponse, { taskIds: string[]; targetDate: string }>(
        '/api/tasks/bulk-move',
        ptData
      );
    } catch (error) {
      console.error('todoApi.bulkMoveTasks error:', error);
      throw error;
    }
  }
};
